import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  DocsAnnotation,
  Flex,
  Heading,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  TagBadge,
  Text,
} from '@chatsdet/ui';
import { MCP_URL } from '../../../config.js';
import { BRAND } from '../../../brand';

const CLAUDE_AI_CONNECTOR_URL = 'https://claude.ai/settings/connectors';
const DOCS_URL = 'https://docs.runcofactor.com/integrations/claude';
const MARKETPLACE_REPO = 'TreebeardHQ/cofactor-plugins';
const PLUGIN_SLUG = 'cofactor@cofactor-plugins';

function CodeBlock({ children }: { children: string }) {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard.writeText(children).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => {
        /* clipboard blocked — user can copy manually */
      }
    );
  };
  return (
    <div className="relative">
      <pre className="rounded-md border border-border bg-muted p-4 text-sm font-mono whitespace-pre-wrap break-all">
        {children}
      </pre>
      <button
        type="button"
        onClick={copy}
        className="absolute right-2 top-2 rounded border border-border bg-background px-2 py-1 text-xs hover:bg-muted"
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/**
 * Settings → Integrations → Coding agents (MCP). Install instructions for
 * connecting Claude (and any MCP client) to the org over the hosted MCP server.
 */
export function ClaudeSettingsSection() {
  const name = BRAND.name;
  const mcpUrl = `${MCP_URL}/mcp`;

  return (
    <DocsAnnotation slug="integrations/claude" title="Claude Integration">
      <Card padding="lg">
        <CardHeader>
          <Flex align="start" gap="md">
            <div className="flex-1">
              <Flex align="center" gap="sm">
                <Heading level={3}>Claude Integration</Heading>
                <TagBadge name="MCP" color="blue" hideDot />
              </Flex>
              <Text size="sm" className="text-muted-foreground mt-1">
                Connect Claude to your {name} organisation over MCP. Authentication is handled by
                OAuth — no API keys to manage.
              </Text>
            </div>
          </Flex>
        </CardHeader>
        <CardContent>
          <Tabs defaultSelectedKey="claude-ai">
            <TabList aria-label="Install surfaces">
              <Tab id="claude-ai">Claude.ai</Tab>
              <Tab id="claude-code">Claude Code</Tab>
              <Tab id="other">Other clients</Tab>
            </TabList>

            <TabPanel id="claude-ai" className="mt-6 space-y-4">
              <Heading level={4}>Add {name} as a custom connector</Heading>
              <Text>
                Open Claude.ai connector settings, click <b>Add custom connector</b>, and paste the
                URL below. You'll be redirected to sign in to {name} and pick the organisation Claude
                should access.
              </Text>
              <CodeBlock>{mcpUrl}</CodeBlock>
              <a
                href={CLAUDE_AI_CONNECTOR_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
              >
                Open Claude.ai connector settings
              </a>
            </TabPanel>

            <TabPanel id="claude-code" className="mt-6 space-y-4">
              <Heading level={4}>Install the {name} plugin</Heading>
              <Text>From any directory, run:</Text>
              <CodeBlock>{`/plugin marketplace add ${MARKETPLACE_REPO}\n/plugin install ${PLUGIN_SLUG}`}</CodeBlock>
              <Text>
                The first tool call triggers an OAuth flow in your browser. After consent, the plugin
                stays connected — no further setup.
              </Text>
            </TabPanel>

            <TabPanel id="other" className="mt-6 space-y-4">
              <Heading level={4}>Cursor, Windsurf, and any MCP client</Heading>
              <Text>
                {name} exposes a standards-compliant Streamable HTTP MCP endpoint with OAuth 2.1 +
                PKCE and Dynamic Client Registration. Point your client at:
              </Text>
              <CodeBlock>{mcpUrl}</CodeBlock>
              <Text>
                Discovery documents are served at{' '}
                <code>/.well-known/oauth-authorization-server</code> and{' '}
                <code>/.well-known/oauth-protected-resource</code>.
              </Text>
            </TabPanel>
          </Tabs>

          <div className="mt-6 text-sm text-muted-foreground">
            Need more detail?{' '}
            <a href={DOCS_URL} target="_blank" rel="noreferrer" className="underline">
              Read the integration docs
            </a>
            .
          </div>
        </CardContent>
      </Card>
    </DocsAnnotation>
  );
}
